import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, CalendarPlus, Search, UserPlus, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

const today = () => new Date().toISOString().slice(0, 10);

export default function AddAppointmentModal({ onClose, onCreated }) {
  const [mode, setMode] = useState("member"); // member | walkin
  const [clientQ, setClientQ] = useState("");
  const [client, setClient] = useState(null);
  const [walkinName, setWalkinName] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [enhancerId, setEnhancerId] = useState("");
  const [enhancerName, setEnhancerName] = useState("");
  const [date, setDate] = useState(today());
  const [time, setTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [applyCredit, setApplyCredit] = useState(null); // null=unanswered, false=no, true=yes
  const [creditId, setCreditId] = useState("");
  const [showNewClient, setShowNewClient] = useState(false);
  const [ncFirst, setNcFirst] = useState("");
  const [ncLast, setNcLast] = useState("");
  const [ncEmail, setNcEmail] = useState("");
  const [ncPhone, setNcPhone] = useState("");
  const [ncSaving, setNcSaving] = useState(false);

  const services = useQuery({ queryKey: ["pos-services-add"], queryFn: () => api.get("/pos/services").then((r) => r.data) });
  const enhancers = useQuery({ queryKey: ["pos-enhancers-add"], queryFn: () => api.get("/enhancers").then((r) => r.data) });
  const creditsQ = useQuery({
    queryKey: ["appt-credits", client?.id, serviceId],
    queryFn: () => api.get(`/pos/clients/${client.id}/applicable-credits`, { params: { service_id: serviceId } }).then((r) => r.data),
    enabled: mode === "member" && !!client && !!serviceId,
  });
  const credits = creditsQ.data?.credits || [];
  const clients = useQuery({
    queryKey: ["pos-client-search", clientQ],
    queryFn: () => api.get(`/pos/clients?q=${encodeURIComponent(clientQ)}`).then((r) => r.data),
    enabled: mode === "member" && clientQ.trim().length >= 2,
    staleTime: 0,
  });
  const summaryQ = useQuery({
    queryKey: ["pos-client-summary", client?.id],
    queryFn: () => api.get(`/pos/clients/${client.id}/summary`).then((r) => r.data),
    enabled: mode === "member" && !!client,
  });
  const summary = summaryQ.data;
  const clientLabel = (c) => `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || c.email;

  const createClient = async () => {
    if (!ncFirst.trim() || !ncLast.trim()) { toast.error("First and last name are required."); return; }
    if (!ncEmail.trim() || !ncEmail.includes("@")) { toast.error("A valid email is required."); return; }
    setNcSaving(true);
    try {
      const { data } = await api.post("/pos/clients", { first_name: ncFirst.trim(), last_name: ncLast.trim(), email: ncEmail.trim(), phone: ncPhone.trim() });
      setClient(data); setShowNewClient(false); setClientQ("");
      setNcFirst(""); setNcLast(""); setNcEmail(""); setNcPhone("");
      toast.success(`${clientLabel(data)} is ready — continue the booking.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create client");
    } finally { setNcSaving(false); }
  };

  const svcList = services.data || [];
  const enhList = enhancers.data || [];
  // /pos/services is already center-scoped server-side — no client-side re-filtering.
  const displayServices = svcList;

  const pickService = (id) => {
    setServiceId(id);
    setApplyCredit(null);
    setCreditId("");
    const s = svcList.find((x) => x.id === id);
    if (s) { setEnhancerId(s.enhancer_id || ""); setEnhancerName(s.enhancer_name || ""); }
  };
  const pickEnhancer = (id) => {
    setEnhancerId(id);
    setEnhancerName(id ? (enhList.find((e) => e.id === id)?.name || "") : "");
  };

  const save = async () => {
    if (!serviceId) { toast.error("Please choose a service."); return; }
    if (mode === "member" && !client) { toast.error("Please select a client or switch to walk-in."); return; }
    if (mode === "walkin" && !walkinName.trim()) { toast.error("Please enter the walk-in's name."); return; }
    if (!date || !time) { toast.error("Please choose a date and time."); return; }
    setSaving(true);
    try {
      const body = {
        service_id: serviceId, date, start_time: time, notes,
        enhancer_id: enhancerId, enhancer_name: enhancerName,
      };
      if (mode === "member") {
        body.client_id = client.id;
        if (applyCredit === true) body.credit_id = creditId || credits[0]?.id;
      }
      else { body.name = walkinName.trim(); body.walk_in = true; }
      const { data: res } = await api.post("/pos/appointments", body);
      toast.success(res.package_name
        ? `Booked with a ${res.package_name} credit — ${res.sessions_remaining} session${res.sessions_remaining === 1 ? "" : "s"} left.`
        : "Appointment added to the schedule.");
      onCreated?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === "object" ? d?.message : d) || "Could not create appointment");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="add-appt-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="eyw-label mb-1 flex items-center gap-1.5"><CalendarPlus size={13} /> Front desk</p>
            <h3 className="font-serif text-2xl text-[#1A242B]">Add appointment</h3>
          </div>
          <button onClick={onClose} data-testid="add-appt-close" className="text-[#8FA89B] hover:text-[#1A242B]"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          {/* Client */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-[#1A242B]">Client</label>
              <div className="inline-flex rounded-full bg-[#F1EEE8] p-0.5 text-xs">
                <button onClick={() => { setMode("member"); }} data-testid="add-appt-mode-member" className={`px-3 py-1 rounded-full ${mode === "member" ? "bg-[#144F75] text-white" : "text-[#4A5D6B]"}`}>Member</button>
                <button onClick={() => { setMode("walkin"); setClient(null); }} data-testid="add-appt-mode-walkin" className={`px-3 py-1 rounded-full ${mode === "walkin" ? "bg-[#144F75] text-white" : "text-[#4A5D6B]"}`}>Walk-in</button>
              </div>
            </div>
            {mode === "member" ? (
              client ? (
                <div className="rounded-xl border border-[#E5E0D8] p-3.5 space-y-1.5" data-testid="add-appt-client-selected">
                  <div className="flex items-center justify-between">
                    <span className="text-[#1A242B] font-medium">{summary?.name || clientLabel(client)}</span>
                    <button onClick={() => setClient(null)} data-testid="add-appt-client-change" className="text-xs text-[#B45151]">Change</button>
                  </div>
                  {summary ? (
                    <div className="text-xs text-[#4A5D6B] space-y-0.5" data-testid="add-appt-client-details">
                      {summary.birthday_week && (
                        <p className="text-[#B5794E]" data-testid="add-appt-birthday">🎂 Birthday week — wish them well!{summary.birthday_perk ? ` ${summary.birthday_perk.label} applies at the register.` : ""}</p>
                      )}
                      <p>{summary.email}{summary.phone ? ` · ${summary.phone}` : ""}</p>
                      <p><span className="text-[#8FA89B]">Membership:</span> {summary.membership}{summary.store_credit > 0 ? ` · Store credit ${money(summary.store_credit)}` : ""}</p>
                      {summary.packages.length > 0 ? (
                        <p><span className="text-[#8FA89B]">Packages:</span> {summary.packages.map((p) => `${p.package_name} (${p.sessions_remaining}/${p.sessions_total} left)`).join(", ")}</p>
                      ) : (
                        <p className="text-[#8FA89B]">No active packages</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-[#8FA89B]"><Loader2 className="animate-spin inline" size={11} /> Loading details…</p>
                  )}
                </div>
              ) : showNewClient ? (
                <div className="rounded-xl border border-[#E5E0D8] p-3.5 space-y-2" data-testid="add-appt-new-client-form">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[#1A242B]">New client</p>
                    <button onClick={() => setShowNewClient(false)} data-testid="add-appt-new-client-cancel" className="text-xs text-[#8FA89B]">Back to search</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={ncFirst} onChange={(e) => setNcFirst(e.target.value)} placeholder="First name" data-testid="add-appt-nc-first" className="eyw-input w-full text-sm" />
                    <input value={ncLast} onChange={(e) => setNcLast(e.target.value)} placeholder="Last name" data-testid="add-appt-nc-last" className="eyw-input w-full text-sm" />
                  </div>
                  <input value={ncEmail} onChange={(e) => setNcEmail(e.target.value)} placeholder="Email" type="email" data-testid="add-appt-nc-email" className="eyw-input w-full text-sm" />
                  <input value={ncPhone} onChange={(e) => setNcPhone(e.target.value)} placeholder="Phone (optional)" data-testid="add-appt-nc-phone" className="eyw-input w-full text-sm" />
                  <button onClick={createClient} disabled={ncSaving} data-testid="add-appt-nc-save" className="eyw-btn w-full !py-2 text-xs disabled:opacity-60">
                    {ncSaving ? <Loader2 className="animate-spin" size={13} /> : <UserPlus size={13} />} Create client & continue
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
                  <input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Search members by name or email…" data-testid="add-appt-client-search" className="eyw-input w-full !pl-9" />
                  {clientQ.trim().length >= 2 && (
                    <div className="mt-1 max-h-52 overflow-y-auto rounded-xl border border-[#E5E0D8] divide-y divide-[#F1EEE8]" data-testid="add-appt-client-results">
                      {clients.isLoading ? <div className="p-3 text-sm text-[#8FA89B]"><Loader2 className="animate-spin inline" size={14} /> Searching…</div>
                        : (clients.data || []).length === 0 ? <div className="p-3 text-sm text-[#8FA89B]">No matches found.</div>
                        : (clients.data || []).map((c) => (
                          <button key={c.id} onClick={() => { setClient(c); setClientQ(""); }} data-testid={`add-appt-client-${c.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9F7F3] flex items-center justify-between">
                            <span className="text-[#1A242B]">{clientLabel(c)}{c.email ? <span className="text-[#8FA89B]"> · {c.email}</span> : null}</span>
                            <Check size={14} className="text-[#5C8A72] opacity-0" />
                          </button>
                        ))}
                      <button onClick={() => { setShowNewClient(true); setNcEmail(clientQ.includes("@") ? clientQ.trim() : ""); }} data-testid="add-appt-new-client-btn"
                        className="w-full text-left px-3 py-2 text-sm text-[#144F75] hover:bg-[#F9F7F3] flex items-center gap-1.5">
                        <UserPlus size={13} /> New client…
                      </button>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="relative">
                <UserPlus size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
                <input value={walkinName} onChange={(e) => setWalkinName(e.target.value)} placeholder="Walk-in name" data-testid="add-appt-walkin-name" className="eyw-input w-full !pl-9" />
              </div>
            )}
          </div>

          {/* Service */}
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Service / offering</label>
            <select value={serviceId} onChange={(e) => pickService(e.target.value)} data-testid="add-appt-service" className="eyw-input w-full">
              <option value="">Choose a service…</option>
              {displayServices.map((s) => <option key={s.id} value={s.id}>{s.title}{s.price ? ` — ${money(Number(s.price))}` : ""}</option>)}
            </select>
          </div>

          {/* Enhancer */}
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Enhancer <span className="text-[#8FA89B] font-normal">(if applicable)</span></label>
            <select value={enhancerId} onChange={(e) => pickEnhancer(e.target.value)} data-testid="add-appt-enhancer" className="eyw-input w-full">
              <option value="">— None —</option>
              {enhList.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            <p className="text-[11px] text-[#8FA89B] mt-1">We'll block double-booking across all centers automatically.</p>
          </div>

          {/* Date + time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="add-appt-date" className="eyw-input w-full" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A242B] mb-1 block">Time</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="add-appt-time" className="eyw-input w-full" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Notes <span className="text-[#8FA89B] font-normal">(optional)</span></label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the enhancer should know…" data-testid="add-appt-notes" className="eyw-input w-full" />
          </div>

          {/* Package credit prompt */}
          {mode === "member" && client && serviceId && credits.length > 0 && (
            <div className="rounded-xl border border-[#5C8A72]/30 bg-[#5C8A72]/[0.06] p-3.5" data-testid="add-appt-credit-prompt">
              <p className="text-sm text-[#1A242B]">
                This client has <b>{(credits.find((c) => c.id === (creditId || credits[0].id)) || credits[0]).sessions_remaining} session{(credits.find((c) => c.id === (creditId || credits[0].id)) || credits[0]).sessions_remaining === 1 ? "" : "s"} remaining</b> on their <b>{(credits.find((c) => c.id === (creditId || credits[0].id)) || credits[0]).package_name}</b> package. Apply a credit to this booking?
              </p>
              {credits.length > 1 && (
                <select value={creditId || credits[0].id} onChange={(e) => setCreditId(e.target.value)} data-testid="add-appt-credit-select" className="eyw-input w-full text-sm mt-2">
                  {credits.map((c) => <option key={c.id} value={c.id}>{c.package_name} — {c.sessions_remaining}/{c.sessions_total} left{c.expires_at ? ` · expires ${c.expires_at}` : ""}</option>)}
                </select>
              )}
              <div className="flex gap-2 mt-2.5">
                <button type="button" onClick={() => setApplyCredit(true)} data-testid="add-appt-credit-yes"
                  className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${applyCredit === true ? "bg-[#5C8A72] text-white border-[#5C8A72]" : "border-[#5C8A72]/40 text-[#5C8A72] hover:bg-[#5C8A72]/10"}`}>
                  Yes — use a credit
                </button>
                <button type="button" onClick={() => setApplyCredit(false)} data-testid="add-appt-credit-no"
                  className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${applyCredit === false ? "bg-[#4A5D6B] text-white border-[#4A5D6B]" : "border-[#E5E0D8] text-[#4A5D6B] hover:bg-[#F1EEE8]"}`}>
                  No — standard payment
                </button>
              </div>
              {applyCredit === true && <p className="text-[11px] text-[#5C8A72] mt-1.5">Booking will be marked as covered by package — no charge.</p>}
            </div>
          )}

          <button onClick={save} disabled={saving} data-testid="add-appt-save" className="eyw-btn w-full !py-3 text-sm disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <CalendarPlus size={15} />} Add appointment
          </button>
        </div>
      </div>
    </div>
  );
}
